/**
 * @fileOverview
 * Adapts the `studies/links/` payloads to the camelCase view model the Linked Studies graph
 * binds to — the same anti-corruption boundary study-log.util.ts draws for the logbook.
 */
import {
  StudyLinkApiEdge,
  StudyLinkEdge,
  StudyLinkResponse,
  StudyLinksApiResponse
} from '../models/study-link.model';

/**
 * Maps one linkage onto the pair the graph indexes.
 *
 * The two names are read out by index rather than spread: spreading widens the tuple to
 * `string[]`, and the graph relies on a linkage having exactly two endpoints.
 */
export function toStudyLinkEdge(link: StudyLinkApiEdge): StudyLinkEdge {
  return { studyNames: [link.study_names[0], link.study_names[1]] };
}

/**
 * Maps a whole linkage payload, tolerating a response that arrives without the list.
 *
 * `auditTrail` is carried through only when the response holds one — a linkage edit
 * returns it for the loaded study, the plain read never does, and an absent trail must
 * not be mistaken for an empty one.
 */
export function toStudyLinkResponse(response: StudyLinksApiResponse | null): StudyLinkResponse {
  const links = (response?.links ?? []).map(toStudyLinkEdge);
  return response?.audit_trail ? { links, auditTrail: response.audit_trail } : { links };
}

/**
 * The studies linked to `studyName`.
 *
 * A linkage is undirected and stored as a single normalized row, so the study can sit on either
 * side of the pair and its partner is whichever endpoint is not it. The backend refuses
 * self-links, so no name can appear as its own neighbor.
 */
export function neighborsOf(links: StudyLinkEdge[], studyName: string): string[] {
  const neighbors: string[] = [];
  for (const link of links) {
    const [first, second] = link.studyNames;
    if (first === studyName) {
      neighbors.push(second);
    } else if (second === studyName) {
      neighbors.push(first);
    }
  }
  return neighbors;
}
